/* Pulse Hero Slideshow controller.
   Per-section state machine: auto-advance via setTimeout, indicator
   fill via CSS transition driven by `is-running` class. Idempotent —
   re-binding is a no-op via `dataset.bound`. */
(function () {
  function setup(section) {
    if (!section || section.dataset.bound === 'true') return;
    section.dataset.bound = 'true';

    var slides = section.querySelectorAll('.pulse-hero-slideshow__slide');
    var indicators = section.querySelectorAll('.pulse-hero-slideshow__indicator');
    var slideCount = slides.length;
    if (slideCount <= 1) return;

    var rootStyle = window.getComputedStyle(section);
    var durationStr = rootStyle.getPropertyValue('--phs-slide-duration') || '6s';
    var durationMs = parseFloat(durationStr) * 1000;
    if (isNaN(durationMs) || durationMs <= 0) durationMs = 6000;

    var currentIdx = 0;
    var timer = null;
    var isPlaying = true;
    /* `slideStartedAt` is the timestamp when the current slide became
       active. Combined with `pausedElapsed` (accumulated paused-fill
       time), it lets us resume the indicator fill from its frozen
       position when the timer restarts after IntersectionObserver or
       Page Visibility re-engages. */
    var slideStartedAt = 0;
    var pausedElapsed = 0;
    /* `userPaused` tracks whether the visitor *explicitly* paused via the
       button. Hover-pause respects this so a manual pause survives a
       hover cycle (without it, hovering off the section restarts a
       paused slideshow, undermining the user's intent). */
    var userPaused = false;
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) isPlaying = false;

    function applyActive(idx, direction) {
      // direction: 1 = forward (next), -1 = backward (prev), undefined = forward.
      // Forward: incoming from RIGHT, outgoing to LEFT (default).
      // Backward: incoming from LEFT, outgoing to RIGHT — match the user's
      //   swipe direction so nav reads naturally.
      var prevActive = null;
      for (var p = 0; p < slides.length; p++) {
        if (slides[p].classList.contains('is-active')) { prevActive = slides[p]; break; }
      }
      var goingBack = direction === -1;

      // Backward direction: snap the incoming slide to off-LEFT (the
      // direction it'll appear to come from) WITHOUT a transition.
      // Then later the is-active class triggers the slide to 0,
      // animating it in from the left. The forced reflow commits the
      // snap before the next class change.
      if (goingBack && prevActive && slides[idx] !== prevActive) {
        var newSlide = slides[idx];
        newSlide.classList.add('phs-no-transition');
        newSlide.classList.add('is-snap-left');
        void newSlide.offsetWidth;
        newSlide.classList.remove('phs-no-transition');
        newSlide.classList.remove('is-snap-left');
      }

      for (var i = 0; i < slides.length; i++) {
        var becoming = i === idx;
        slides[i].classList.toggle('is-active', becoming);
        // Pause every non-active video; play (and reset to 0) the
        // active one. Browsers throttle background videos but we want
        // a clean restart per activation for a deliberate feel.
        var vids = slides[i].querySelectorAll('video');
        for (var v = 0; v < vids.length; v++) {
          var video = vids[v];
          if (becoming) {
            try { video.currentTime = 0; } catch (err) {}
            var p2 = video.play();
            if (p2 && typeof p2.catch === 'function') p2.catch(function () {});
          } else {
            try { video.pause(); } catch (err) {}
          }
        }
      }

      // Tag the outgoing slide so it animates off in the correct
      // direction. Forward = leaves to the LEFT; backward = leaves
      // to the RIGHT. After the transition completes, snap the
      // leaving slide back to its resting position WITHOUT a
      // transition so it doesn't flash across the viewport.
      if (prevActive && prevActive !== slides[idx]) {
        if (goingBack) prevActive.classList.add('is-going-back');
        prevActive.classList.add('is-leaving');
        setTimeout(function (slide) {
          return function () {
            slide.classList.add('phs-no-transition');
            slide.classList.remove('is-leaving');
            slide.classList.remove('is-going-back');
            void slide.offsetWidth;
            slide.classList.remove('phs-no-transition');
          };
        }(prevActive), 700);
      }

      for (var k = 0; k < indicators.length; k++) {
        var on = k === idx;
        indicators[k].classList.toggle('is-active', on);
        indicators[k].setAttribute('aria-selected', on ? 'true' : 'false');
      }
    }

    function startFill() {
      // Toggle the running class off then on across rAF so the CSS
      // transition restarts (otherwise switching back to `is-running`
      // on the same element wouldn't replay the transition).
      var active = section.querySelector('.pulse-hero-slideshow__indicator.is-active');
      if (!active) return;
      active.classList.remove('is-running');
      var fill = active.querySelector('.pulse-hero-slideshow__indicator-fill');
      if (fill) fill.style.transform = 'scaleX(0)';
      // Force layout flush so the next frame sees the reset.
      void active.offsetWidth;
      requestAnimationFrame(function () {
        if (fill) fill.style.removeProperty('transform');
        active.classList.add('is-running');
      });
    }

    function freezeFill() {
      var active = section.querySelector('.pulse-hero-slideshow__indicator.is-active');
      if (!active) return;
      var fill = active.querySelector('.pulse-hero-slideshow__indicator-fill');
      if (!fill) return;
      // Capture the computed scaleX at the moment of pause and pin it
      // inline so it doesn't snap when we drop `is-running`.
      var matrix = window.getComputedStyle(fill).transform;
      active.classList.remove('is-running');
      if (matrix && matrix !== 'none') fill.style.transform = matrix;
      // Track elapsed so the resume scheduleNext shortens the timer to
      // the remaining slice instead of restarting at 0.
      if (slideStartedAt) pausedElapsed += (Date.now() - slideStartedAt);
    }

    function next() {
      currentIdx = (currentIdx + 1) % slideCount;
      pausedElapsed = 0;  // fresh slide — restart the elapsed counter
      applyActive(currentIdx, 1);
      if (isPlaying) scheduleNext();
    }

    function scheduleNext() {
      clearTimeout(timer);
      var remaining = durationMs - pausedElapsed;
      if (remaining <= 0) remaining = durationMs;
      if (pausedElapsed > 0) {
        // Resume the existing fill from its frozen scaleX with the
        // remaining time. The active indicator's fill was pinned by
        // freezeFill — pick it back up by adding `is-running` and
        // setting a one-off transition to the remaining duration.
        var active = section.querySelector('.pulse-hero-slideshow__indicator.is-active');
        if (active) {
          var fill = active.querySelector('.pulse-hero-slideshow__indicator-fill');
          if (fill) {
            // Read the captured matrix (set inline by freezeFill).
            var matrix = fill.style.transform || window.getComputedStyle(fill).transform;
            // Force layout flush so the restart frame sees the pinned matrix.
            void active.offsetWidth;
            requestAnimationFrame(function () {
              // Strip inline transform; CSS rules will drive the rest.
              fill.style.removeProperty('transform');
              fill.style.transition = 'transform ' + remaining + 'ms linear';
              active.classList.add('is-running');
              // After the override completes, clear inline transition so
              // the next CSS-driven start uses the default selector rule.
              setTimeout(function () { fill.style.removeProperty('transition'); }, remaining + 50);
            });
          }
        }
      } else {
        startFill();
      }
      slideStartedAt = Date.now();
      timer = setTimeout(function () {
        pausedElapsed = 0;
        next();
      }, remaining);
    }

    function pause() {
      if (!isPlaying) return;
      isPlaying = false;
      clearTimeout(timer);
      timer = null;
      freezeFill();
    }

    function play() {
      if (isPlaying) return;
      isPlaying = true;
      scheduleNext();
    }

    function goTo(idx, direction) {
      if (idx < 0 || idx >= slideCount || idx === currentIdx) return;
      // Auto-detect direction from idx comparison if not given.
      if (direction === undefined) direction = idx > currentIdx ? 1 : -1;
      currentIdx = idx;
      pausedElapsed = 0;  // user jump — fresh fill timer
      applyActive(idx, direction);
      if (isPlaying) scheduleNext();
    }

    // Public API attached to the section element so other scripts /
    // the editor's section:load handler can drive it deterministically.
    section.pulseSlideshow = { play: play, pause: pause, goTo: goTo, next: next };

    /* Pause/play button — toggles the auto-advance and updates the
       data-state attribute that the CSS uses to swap the icon. */
    var playPauseBtn = section.querySelector('[data-slideshow-playpause]');
    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', function () {
        if (isPlaying) {
          userPaused = true;
          pause();
          playPauseBtn.setAttribute('data-state', 'paused');
          playPauseBtn.setAttribute('aria-label', 'Play slideshow');
        } else {
          userPaused = false;
          play();
          playPauseBtn.setAttribute('data-state', 'playing');
          playPauseBtn.setAttribute('aria-label', 'Pause slideshow');
        }
      });
    }

    /* Indicator click — jumps to the matching slide and resets the fill. */
    for (var n = 0; n < indicators.length; n++) {
      (function (idx, el) {
        el.addEventListener('click', function () { goTo(idx); });
      })(n, indicators[n]);
    }

    /* Keyboard nav — only when something inside the section has focus,
       so arrow keys elsewhere on the page don't interfere with normal
       browser scrolling/text input. Space toggles play/pause; ←/→
       step manually. */
    section.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo((currentIdx + 1) % slideCount, 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo((currentIdx - 1 + slideCount) % slideCount, -1);
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        if (e.target && e.target.matches && e.target.matches('[data-slideshow-playpause]')) {
          // Let the click handler on the button manage Space —
          // browsers fire click on space natively for buttons.
          return;
        }
        // Otherwise treat Space as toggle.
        e.preventDefault();
        if (isPlaying) pause(); else play();
      }
    });

    /* Touch swipe — left/right swipe steps slides on mobile. We track
       only the first finger; >30px horizontal movement (and clearly
       horizontal vs vertical) triggers a step. Vertical swipes pass
       through to scroll the page. */
    var touchStartX = 0;
    var touchStartY = 0;
    var touchActive = false;
    section.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { touchActive = false; return; }
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchActive = true;
    }, { passive: true });
    section.addEventListener('touchend', function (e) {
      if (!touchActive) return;
      touchActive = false;
      var t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      var dx = t.clientX - touchStartX;
      var dy = t.clientY - touchStartY;
      if (Math.abs(dx) < 30) return;          // not a swipe
      if (Math.abs(dy) > Math.abs(dx)) return; // vertical-dominant: ignore
      // Swipe right→left (dx negative) = go to NEXT, slides come from
      // the right. Swipe left→right (dx positive) = go to PREV, slides
      // come from the left.
      if (dx < 0) goTo((currentIdx + 1) % slideCount, 1);
      else goTo((currentIdx - 1 + slideCount) % slideCount, -1);
    }, { passive: true });

    /* Section-visibility gate. The timer should only run when the
       slideshow is actually on screen — otherwise we waste CPU and the
       fill animation runs invisibly. We track inView and tabActive,
       and require BOTH to be true to keep the timer alive. */
    var inView = false;
    var tabActive = !document.hidden;

    function reconcile() {
      if (!isPlaying) return;
      if (inView && tabActive) {
        if (!timer) scheduleNext();
      } else {
        if (timer) {
          clearTimeout(timer);
          timer = null;
          freezeFill();
        }
      }
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          inView = entries[k].isIntersecting;
        }
        reconcile();
      }, { threshold: 0.1 });
      io.observe(section);
    } else {
      inView = true;
    }

    document.addEventListener('visibilitychange', function () {
      tabActive = !document.hidden;
      reconcile();
    });

    /* Pause-on-hover (merchant opt-in via the section setting). We
       pause/play directly without flipping the visible button state —
       this is automatic hover-pause, not a user toggle. */
    if (section.dataset.pauseOnHover === 'true') {
      section.addEventListener('mouseenter', function () {
        if (isPlaying && !userPaused) pause();
      });
      section.addEventListener('mouseleave', function () {
        if (!userPaused) play();
      });
    }

    applyActive(0);
    /* Don't call scheduleNext() directly — the IntersectionObserver
       above will reconcile and start the timer the moment the section
       enters the viewport. Browsers fire the IO callback once on
       observe even if the section is already visible, so this still
       starts immediately on initial paint. */
  }

  function init(root) {
    var nodes = (root || document).querySelectorAll('[data-section-type="pulse-hero-slideshow"]');
    for (var i = 0; i < nodes.length; i++) setup(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  /* Editor-only — when the merchant clicks a slide block in the customizer
     sidebar, jump the slideshow to that slide and pause auto-advance so
     they can see what they're editing. After ANY block-setting change
     Shopify re-renders the whole section (re-runs init() → snaps back
     to slide 0). The customizer then fires `shopify:block:select` for
     the block they were editing, which this listener catches to put the
     correct slide back into view — making the post-edit "snap back to
     slide 0" feel invisible to the merchant. */
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target;
    if (!slide || !slide.classList || !slide.classList.contains('pulse-hero-slideshow__slide')) return;
    var section = slide.closest('[data-section-type="pulse-hero-slideshow"]');
    if (!section || !section.pulseSlideshow) return;
    var idx = parseInt(slide.getAttribute('data-slide-index'), 10);
    if (isNaN(idx)) return;
    section.pulseSlideshow.pause();
    section.pulseSlideshow.goTo(idx);
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var slide = e.target;
    if (!slide || !slide.classList || !slide.classList.contains('pulse-hero-slideshow__slide')) return;
    var section = slide.closest('[data-section-type="pulse-hero-slideshow"]');
    if (!section || !section.pulseSlideshow) return;
    section.pulseSlideshow.play();
  });

  /* Scroll cue — smooth-scrolls past the slideshow section on click. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-pulse-slideshow-scroll-cue]');
    if (!btn) return;
    var section = btn.closest('[data-section-type="pulse-hero-slideshow"]');
    if (!section) return;
    var rect = section.getBoundingClientRect();
    var target = rect.bottom + window.scrollY;
    window.scrollTo({ top: target, behavior: 'smooth' });
  });

  /* Whole-slide clickable: JS-delegated navigation.
     Listens for clicks anywhere; navigates ONLY when the click
     target is inside a slide with `data-slide-link` AND not on a
     header element / button / inner link / control button. This
     sidesteps the iOS hit-test quirk where an overlay `<a>` could
     intercept clicks meant for the header above. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var slide = e.target.closest('.pulse-hero-slideshow__slide[data-slide-link]');
    if (!slide) return;
    /* Only the active slide is interactive — stacked siblings should
       never navigate even if their data-slide-link attribute is present. */
    if (!slide.classList.contains('is-active')) return;
    /* Bail if the click hit any interactive element. */
    if (e.target.closest('a, button, summary, input, label, [role="button"], header-drawer, .header-wrapper, .section-header')) return;
    var url = slide.getAttribute('data-slide-link');
    if (url) window.location.href = url;
  });
})();
